/**
 * Fait : donne la structure commune à toute page du produit — lien d'évitement,
 *   en-tête, régions repérables au lecteur d'écran, et un contenu principal
 *   qui peut recevoir le focus. Écrit une fois pour que l'accessibilité ne
 *   dépende pas de ce dont on se souvient page après page.
 *
 * Ne fait pas : il ne met en forme aucun contenu, ne décide d'aucune navigation
 *   et ne connaît aucune donnée. C'est une charpente, pas une page.
 */

import type { ReactNode } from 'react';

export interface GabaritDePageProps {
  /** Titre de la page. Il devient le seul `h1`, ce qui oriente la lecture. */
  readonly titre: string;

  /** Phrase d'explication sous le titre, facultative. */
  readonly sousTitre?: string | undefined;

  /** Barre de commandes de la page, annoncée comme une navigation. */
  readonly commandes?: ReactNode;

  /** Nom de la barre de commandes, lu à voix haute. */
  readonly nomDesCommandes?: string;

  /**
   * La sortie — le bouton « Se déconnecter ». DERNIÈRE dans le document,
   * donc dernière du parcours clavier, mais AFFICHÉE en haut à droite du
   * bandeau par la grille : l'œil la trouve à l'écart des tâches, le clavier
   * la trouve à la fin, et les deux ont raison.
   */
  readonly sortie?: ReactNode;

  readonly children: ReactNode;
}

/**
 * La charpente d'une page.
 *
 * `tabIndex={-1}` sur le contenu principal n'est pas un détail : sans lui, le
 * lien d'évitement déplacerait la vue sans déplacer le focus, et un utilisateur
 * au clavier repartirait du haut au `Tab` suivant.
 */
export function GabaritDePage({
  titre,
  sousTitre,
  commandes,
  nomDesCommandes = 'Commandes de la page',
  sortie,
  children,
}: GabaritDePageProps) {
  return (
    <>
      <a className="lien-evitement" href="#contenu">
        Aller au contenu
      </a>

      {/*
       * UNE VRAIE BARRE, refaite le 2026-08-28 : le bandeau pleine largeur en
       * encre inversée porte l'identité (nom du produit, qui l'on est), la
       * barre des tâches en dessous, et la sortie en haut à droite — une
       * grille place, l'ordre du document reste honnête pour le clavier.
       *
       * Son CONTENU reste dans la colonne du contenu : désaligné, il se lisait
       * comme un élément étranger posé à côté des parties.
       */}
      <header className="entete-de-page">
        <div className="entete-dedans">
          <div className="identite-de-lentete">
            <h1>{titre}</h1>
            {sousTitre !== undefined && (
              <p className="identite-de-page texte-attenue">{sousTitre}</p>
            )}
          </div>
          {commandes !== undefined && (
            <nav aria-label={nomDesCommandes}>
              <ul className="barre-de-commandes">{commandes}</ul>
            </nav>
          )}
          {sortie !== undefined && <div className="sortie-de-lentete">{sortie}</div>}
        </div>
      </header>

      <main id="contenu" tabIndex={-1}>
        {children}
      </main>
    </>
  );
}
